/**
 * Step Indicator Component
 *
 * Shows the current step in the wizard with progress visualization.
 * Dynamically renders steps based on the current calculation mode.
 * Fully responsive — fits any container width without overflow.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { WizardStep } from '../../types';
import './StepIndicator.css';

interface Step {
  id: WizardStep;
  label: string;
}

interface StepIndicatorProps {
  currentStep: WizardStep;
  completedSteps: WizardStep[];
  onStepClick?: (step: WizardStep) => void;
  stepOrder?: WizardStep[];
}

const DEFAULT_STEPS: WizardStep[] = ['mode', 'walls', 'ceiling', 'dimensions', 'application', 'leadCapture'];

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
  completedSteps,
  onStepClick,
  stepOrder = DEFAULT_STEPS,
}) => {
  const { t } = useTranslation('wizard');
  // Labels come from the dictionary keyed by the stable WizardStep id, so the
  // indicator needs no lookup table of its own. Note this is the tightest
  // horizontal space in the app — long translations wrap here first.
  const steps: Step[] = stepOrder.map((id) => ({
    id,
    label: t(`steps.${id}`),
  }));

  const getStepStatus = (stepId: WizardStep): 'completed' | 'active' | 'pending' => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (currentStep === stepId) return 'active';
    return 'pending';
  };

  const isClickable = (stepId: WizardStep): boolean => {
    return completedSteps.includes(stepId) || stepId === currentStep;
  };

  const handleClick = (step: Step) => {
    if (onStepClick && isClickable(step.id)) {
      onStepClick(step.id);
    }
  };

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  return (
    <div className="step-indicator" role="navigation" aria-label={t('wizardProgress')}>
      {/* Row 1: Step x of x: Label — block element, full width, centered */}
      <div className="si-progress-row">
        Step {currentStepIndex + 1} of {steps.length}: {steps[currentStepIndex]?.label}
      </div>

      {/* Row 2: Circles with connector lines, evenly distributed */}
      <div className="si-track">
        {steps.map((step, index) => {
          const status = getStepStatus(step.id);
          const clickable = isClickable(step.id);

          return (
            <React.Fragment key={step.id}>
              {/* Connector line before this step */}
              {index > 0 && (
                <div
                  className={`si-connector ${completedSteps.includes(steps[index - 1].id) ? 'completed' : ''}`}
                  aria-hidden="true"
                />
              )}

              {/* Step circle + label */}
              <div
                className={`si-step ${status} ${clickable ? 'clickable' : ''}`}
                onClick={() => handleClick(step)}
                role="listitem"
                aria-current={status === 'active' ? 'step' : undefined}
              >
                <div className="si-marker">
                  {status === 'completed' ? (
                    <svg
                      className="si-check"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <span className="si-number">{index + 1}</span>
                  )}
                </div>
                <span className="si-label">{step.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default StepIndicator;
